const stripDocumentationUrlSuffix = (message: string): string => {
  const idx = message.lastIndexOf(' - ');
  if (idx === -1) {
    return message;
  }
  const suffix = message.slice(idx + 3);
  return /^https?:\/\/\S+$/.test(suffix) ? message.slice(0, idx) : message;
};

const extractMessage = (body: string): string | undefined => {
  if (!body) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(body) as {
      error?: { message?: string };
      message?: string;
    };
    const message = parsed.error?.message ?? parsed.message;
    return typeof message === 'string' && message
      ? stripDocumentationUrlSuffix(message)
      : undefined;
  } catch {
    return undefined;
  }
};

/** The backend's error name (`error.name` in its JSON body), when it sent one. */
const extractName = (body: string): string | undefined => {
  try {
    const name = (JSON.parse(body) as { error?: { name?: unknown } }).error
      ?.name;
    return typeof name === 'string' ? name : undefined;
  } catch {
    return undefined;
  }
};

/** The portal's backend refusing to read GitHub because the user hasn't connected it. */
const NOT_CONNECTED_ERROR = 'GitHubNotConnectedError';

const summarise = (status: number, body: string, fallback: string): string => {
  const parsedMessage = extractMessage(body);
  return parsedMessage
    ? `${fallback}: ${parsedMessage}`
    : body || `${fallback} (HTTP ${status})`;
};

const explain = (
  status: number,
  summary: string,
  name?: string,
): { detail?: string; retryable: boolean } => {
  // The user's own GitHub isn't connected yet: nothing is misconfigured, and
  // the same request succeeds once they connect.
  if (status === 401 && name === NOT_CONNECTED_ERROR) {
    return { retryable: true };
  }

  if (status === 401) {
    return {
      detail:
        "the backend's GitHub credential is missing or invalid. This is a " +
        'backend configuration problem (e.g. a GitHub App installation token ' +
        'that failed to mint), not something retrying from the browser can fix.',
      retryable: false,
    };
  }

  if (status === 403 && /rate limit/i.test(summary)) {
    return {
      detail:
        'the GitHub API rate limit was reached. Wait a few minutes and try again.',
      retryable: true,
    };
  }

  if (status === 403) {
    return {
      detail:
        'the GitHub credential is authenticated but lacks permission for this action.',
      retryable: false,
    };
  }

  // 4xx describes a request that will fail identically next time; 5xx and
  // network-level failures are worth another go.
  return { retryable: status >= 500 || status === 429 };
};

/**
 * Turns a failed GitHub REST response into a message a user can act on,
 * instead of the raw `{"message": "...", "documentation_url": "..."}` body.
 *
 * Every plugin that talks to GitHub (via the `applicationPortal`/`agentProxy`
 * backend plugins, or a GitHub App token) hits the same handful of failure
 * shapes, so this lives in the React-free `@vivicta/github-config` package
 * rather than being reimplemented per plugin.
 *
 * Prefer `githubApiError` for anything that ends up on screen — it keeps the
 * operator guidance out of the headline instead of concatenating it in.
 */
export const describeGitHubApiError = (
  status: number,
  body: string,
  fallback = 'GitHub request failed',
): string => {
  const summary = summarise(status, body, fallback);
  const { detail } = explain(status, summary, extractName(body));
  return detail ? `${summary} — ${detail}` : summary;
};

/**
 * A thrown GitHub failure split the way the UI needs to show it: a headline
 * anyone can read, the operator-facing cause behind a disclosure, and whether
 * re-issuing the request could plausibly succeed.
 *
 * The extra fields are read structurally by `ErrorState` in `@vivicta/ui`;
 * keep the names in step with `ErrorPresentation` there.
 */
export type GitHubApiError = Error & {
  status: number;
  /** The user hasn't connected their GitHub account yet. */
  notConnected: boolean;
  detail?: string;
  retryable: boolean;
};

export const githubApiError = (
  status: number,
  body: string,
  fallback = 'GitHub request failed',
): GitHubApiError => {
  const summary = summarise(status, body, fallback);
  const name = extractName(body);
  return Object.assign(new Error(summary), {
    status,
    notConnected: status === 401 && name === NOT_CONNECTED_ERROR,
    ...explain(status, summary, name),
  });
};

/**
 * True for a GitHub 404, e.g. "this file/repo doesn't exist yet". Checks the
 * `status` set by `githubApiError` — its message reads "… Not Found" and does
 * not contain the number, so matching the message misses every 404.
 */
export const isGitHubNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (error as { status?: unknown }).status === 404;

/** True when the portal refused because the user hasn't connected GitHub yet. */
export const isGitHubNotConnected = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { notConnected?: unknown }).notConnected === true;
